/**
 * Filling documents (記入書類) section of Check Submissions.
 * Table layout and preview/review flow aligned with CheckSubmissionsVariousCopies.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Submission } from '../types';
import { API_BASE_URL } from '../../../../config';
import {
  fetchChatFlow,
  type ChatFlowFormFillRound,
  type ChatFlowFormToFillItem,
  type ChatFlowDocumentItem,
  type ChatFlowProfileSectionItem,
} from '../../../../services/chatFlowApi';
import approveIcon from '../../../../assests/icons/approved.png';
import rejectIcon from '../../../../assests/icons/rejected.png';
import folderIcon from '../../../../assests/icons/folder.png';
import commentIcon from '../../../../assests/icons/comment.png';
import FilledFormPreview from './FilledFormPreview';
import ProfileSectionPreview from './ProfileSectionPreview';
import {
  buildResubmissionEmailContent,
  type BuiltResubmissionEmail,
  type ResubmissionTemplatesOverride,
} from './resubmissionManagement/emailTemplates';
import {
  fillingRoadmapFromChatFlow,
  formHasCompletedRound,
  profileSectionNeedsReview,
  type FillingRoadmapState,
} from './fillingRoadmap';

function getStatusLabel(status: string | undefined): string {
  switch (status) {
    case 'completed':
    case 'final_confirm':
      return '記入済み';
    case 'in_progress':
      return '記入中';
    case 'notyet':
    default:
      return '未記入';
  }
}

export interface CheckSubmissionsFilledDocumentsProps {
  submission?: Submission | null;
  archiveMode?: boolean;
  /** When set (archive period tab), load this invitation instead of latest. */
  forcedInvitationId?: string | null;
  onFillingStateChange?: (state: FillingRoadmapState) => void;
  onRequestResubmissionEmail?: (built: BuiltResubmissionEmail, invitationId: string) => void;
  resubmissionTemplateOverride?: ResubmissionTemplatesOverride | null;
}

const CheckSubmissionsFilledDocuments: React.FC<CheckSubmissionsFilledDocumentsProps> = ({
  submission,
  archiveMode = false,
  forcedInvitationId = null,
  onFillingStateChange,
  onRequestResubmissionEmail,
  resubmissionTemplateOverride,
}) => {
  const [forms, setForms] = useState<ChatFlowFormToFillItem[]>([]);
  const [profileSections, setProfileSections] = useState<ChatFlowProfileSectionItem[]>([]);
  const [variousDocuments, setVariousDocuments] = useState<ChatFlowDocumentItem[]>([]);
  const [invitationId, setInvitationId] = useState('');
  const [loading, setLoading] = useState(true);
  const [commentPopupForm, setCommentPopupForm] = useState<ChatFlowFormToFillItem | null>(null);
  const [commentPopupSection, setCommentPopupSection] = useState<ChatFlowProfileSectionItem | null>(null);
  const [previewSelection, setPreviewSelection] = useState<{
    form: ChatFlowFormToFillItem;
    round: ChatFlowFormFillRound;
  } | null>(null);
  const [profilePreviewSection, setProfilePreviewSection] = useState<ChatFlowProfileSectionItem | null>(null);
  const [showResubmitModal, setShowResubmitModal] = useState(false);
  const [resubmitReason, setResubmitReason] = useState('');

  const loadForms = useCallback(() => {
    const email = submission?.email?.trim();
    if (!email) {
      setForms([]);
      setProfileSections([]);
      setVariousDocuments([]);
      setInvitationId('');
      setLoading(false);
      onFillingStateChange?.({ loading: false, completed: 0, total: 0, reviewed: 0 });
      return;
    }
    setLoading(true);
    onFillingStateChange?.({ loading: true, completed: 0, total: 0, reviewed: 0 });
    fetchChatFlow(email, 'ja', forcedInvitationId)
      .then((res) => {
        const list = res?.formsToFill ?? [];
        setForms(list);
        setProfileSections(res?.profileSections ?? []);
        setVariousDocuments(res?.documents ?? []);
        setInvitationId(res?.invitationId ?? forcedInvitationId?.trim() ?? '');
        onFillingStateChange?.(fillingRoadmapFromChatFlow(res));
      })
      .catch(() => {
        setForms([]);
        setProfileSections([]);
        setVariousDocuments([]);
        setInvitationId('');
        onFillingStateChange?.({ loading: false, completed: 0, total: 0, reviewed: 0 });
      })
      .finally(() => setLoading(false));
  }, [submission?.email, forcedInvitationId, onFillingStateChange]);

  useEffect(() => {
    loadForms();
  }, [loadForms]);

  const formsNeedingReview = forms.filter(formHasCompletedRound);
  const profileNeedingReview = profileSections.filter(profileSectionNeedsReview);
  const allFormsReviewed =
    formsNeedingReview.length === 0 ||
    formsNeedingReview.every((f) => f.adminApproved === true || f.adminApproved === false);
  const allProfileReviewed =
    profileNeedingReview.length === 0 ||
    profileNeedingReview.every((s) => s.adminApproved === true || s.adminApproved === false);
  const canSaveAndProceed = !loading && allFormsReviewed && allProfileReviewed;

  const openResubmissionEmail = () => {
    if (!onRequestResubmissionEmail || !invitationId.trim()) return;
    const name = submission?.empName?.trim() ?? '';
    onRequestResubmissionEmail(
      buildResubmissionEmailContent(
        variousDocuments,
        name,
        resubmissionTemplateOverride ?? undefined,
        forms,
        profileSections
      ),
      invitationId.trim()
    );
  };

  const putFormReview = async (adminApproved: boolean, adminComment: string | null) => {
    if (!API_BASE_URL || !submission?.email?.trim() || !invitationId.trim() || !previewSelection) {
      return false;
    }
    const res = await fetch(`${API_BASE_URL}/api/chat-flow/forms-to-fill/review`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email: submission.email.trim(),
        invitationId: invitationId.trim(),
        formCode: previewSelection.form.formCode,
        submissionRound: previewSelection.round.submissionRound,
        adminApproved,
        adminComment,
      }),
    });
    return res.ok;
  };

  const putProfileSectionReview = async (
    section: ChatFlowProfileSectionItem,
    adminApproved: boolean,
    adminComment: string | null
  ) => {
    if (!API_BASE_URL || !submission?.email?.trim() || !invitationId.trim()) {
      return false;
    }
    const res = await fetch(`${API_BASE_URL}/api/chat-flow/profile-sections/review`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email: submission.email.trim(),
        invitationId: invitationId.trim(),
        section: section.section,
        adminApproved,
        adminComment,
      }),
    });
    return res.ok;
  };

  const hasRows = profileSections.length > 0 || forms.length > 0;

  const renderProfileSectionRow = (section: ChatFlowProfileSectionItem) => {
    const canPreview = profileSectionNeedsReview(section);
    return (
      <tr key={section.section}>
        <td className="check-submissions__td-name">{section.nameJa ?? section.name}</td>
        <td>{getStatusLabel(section.status)}</td>
        <td>
          {canPreview ? (
            <button
              type="button"
              className="check-submissions__icon-btn check-submissions__icon-btn--file"
              aria-label="内容を確認"
              onClick={() => setProfilePreviewSection(section)}
            >
              <img
                src={folderIcon}
                alt=""
                className="check-submissions__icon-img check-submissions__icon-img--folder"
                aria-hidden
              />
            </button>
          ) : (
            <span className="check-submissions__file-dash">—</span>
          )}
        </td>
        <td className="check-submissions__td-approval">
          {section.adminApproved === true && (
            <img src={approveIcon} alt="承認済み" className="check-submissions__approval-icon" aria-hidden />
          )}
          {section.adminApproved === false && (
            <img src={rejectIcon} alt="却下" className="check-submissions__approval-icon" aria-hidden />
          )}
        </td>
        <td className="check-submissions__td-comment">
          {section.adminComment != null && section.adminComment.trim() !== '' && (
            <button
              type="button"
              className="check-submissions__comment-btn"
              aria-label="コメントを見る"
              onClick={() => setCommentPopupSection(section)}
            >
              <img src={commentIcon} alt="" className="check-submissions__comment-icon" aria-hidden />
            </button>
          )}
        </td>
      </tr>
    );
  };

  return (
    <>
      <div className="check-submissions__table-wrapper">
        <div className="check-submissions__table-header">
          <div className="check-submissions__table-title">記入書類</div>
        </div>
        <table className="check-submissions__table">
          <thead>
            <tr>
              <th className="check-submissions__th-name">書類名</th>
              <th>ステータス</th>
              <th>ファイル</th>
              <th>承認</th>
              <th>コメント</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={5} className="check-submissions__td-empty">
                  読み込み中...
                </td>
              </tr>
            ) : !hasRows ? (
              <tr>
                <td colSpan={5} className="check-submissions__td-empty">
                  記入書類がありません
                </td>
              </tr>
            ) : (
              <>
                {profileSections.map(renderProfileSectionRow)}
                {forms.map((form) => (
                  <tr key={form.formCode}>
                    <td className="check-submissions__td-name">{form.nameJa ?? form.name}</td>
                    <td>{getStatusLabel(form.status)}</td>
                    <td>
                      {(form.roundsBySubmission ?? []).length > 0 ? (
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'center',
                            gap: 8,
                            alignItems: 'center',
                            flexWrap: 'wrap',
                          }}
                        >
                          {(form.roundsBySubmission ?? []).map((round) => (
                            <button
                              key={`${form.formCode}_${round.submissionRound}`}
                              type="button"
                              className="check-submissions__icon-btn check-submissions__icon-btn--file"
                              aria-label={`ラウンド${round.submissionRound} のファイルをプレビュー`}
                              onClick={() => setPreviewSelection({ form, round })}
                            >
                              <img
                                src={folderIcon}
                                alt=""
                                className="check-submissions__icon-img check-submissions__icon-img--folder"
                                aria-hidden
                              />
                            </button>
                          ))}
                        </div>
                      ) : (
                        <span className="check-submissions__file-dash">—</span>
                      )}
                    </td>
                    <td className="check-submissions__td-approval">
                      {form.adminApproved === true && (
                        <img src={approveIcon} alt="承認済み" className="check-submissions__approval-icon" aria-hidden />
                      )}
                      {form.adminApproved === false && (
                        <img src={rejectIcon} alt="却下" className="check-submissions__approval-icon" aria-hidden />
                      )}
                    </td>
                    <td className="check-submissions__td-comment">
                      {form.adminComment != null && form.adminComment.trim() !== '' && (
                        <button
                          type="button"
                          className="check-submissions__comment-btn"
                          aria-label="コメントを見る"
                          onClick={() => setCommentPopupForm(form)}
                        >
                          <img src={commentIcon} alt="" className="check-submissions__comment-icon" aria-hidden />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </>
            )}
          </tbody>
        </table>
        {!archiveMode && (
        <div className="check-submissions__footer">
          <button
            type="button"
            className="check-submissions__save-btn"
            disabled={!canSaveAndProceed}
            title={
              !canSaveAndProceed && !loading && (formsNeedingReview.length > 0 || profileNeedingReview.length > 0)
                ? '記入済みの項目をすべて承認または却下してください'
                : undefined
            }
            onClick={openResubmissionEmail}
          >
            保存して次へ進む
          </button>
        </div>
        )}
      </div>

      {commentPopupForm && (
        <div className="check-submissions__comment-popup" role="dialog" aria-modal="true" aria-labelledby="comment-popup-title">
          <div
            className="check-submissions__comment-popup-backdrop"
            onClick={() => setCommentPopupForm(null)}
            aria-hidden
          />
          <div className="check-submissions__comment-popup-box">
            <div className="check-submissions__comment-popup-header">
              <h3 id="comment-popup-title" className="check-submissions__comment-popup-title">
                {commentPopupForm.nameJa ?? commentPopupForm.name} — コメント
              </h3>
              <button
                type="button"
                className="check-submissions__comment-popup-close"
                aria-label="閉じる"
                onClick={() => setCommentPopupForm(null)}
              >
                ×
              </button>
            </div>
            <div className="check-submissions__comment-popup-body">
              <p className="check-submissions__comment-popup-text">{commentPopupForm.adminComment}</p>
            </div>
            <div className="check-submissions__comment-popup-footer">
              <button
                type="button"
                className="check-submissions__comment-popup-ok"
                onClick={() => setCommentPopupForm(null)}
              >
                閉じる
              </button>
            </div>
          </div>
        </div>
      )}

      {commentPopupSection && (
        <div className="check-submissions__comment-popup" role="dialog" aria-modal="true" aria-labelledby="comment-section-popup-title">
          <div
            className="check-submissions__comment-popup-backdrop"
            onClick={() => setCommentPopupSection(null)}
            aria-hidden
          />
          <div className="check-submissions__comment-popup-box">
            <div className="check-submissions__comment-popup-header">
              <h3 id="comment-section-popup-title" className="check-submissions__comment-popup-title">
                {commentPopupSection.nameJa ?? commentPopupSection.name} — コメント
              </h3>
              <button
                type="button"
                className="check-submissions__comment-popup-close"
                aria-label="閉じる"
                onClick={() => setCommentPopupSection(null)}
              >
                ×
              </button>
            </div>
            <div className="check-submissions__comment-popup-body">
              <p className="check-submissions__comment-popup-text">{commentPopupSection.adminComment}</p>
            </div>
            <div className="check-submissions__comment-popup-footer">
              <button
                type="button"
                className="check-submissions__comment-popup-ok"
                onClick={() => setCommentPopupSection(null)}
              >
                閉じる
              </button>
            </div>
          </div>
        </div>
      )}

      {previewSelection && submission?.email && (
        <FilledFormPreview
          form={previewSelection.form}
          activeRound={previewSelection.round}
          employeeEmail={submission.email}
          employeeName={submission.empName}
          invitationId={invitationId}
          readOnly={archiveMode}
          allowResubmit={
            archiveMode
              ? false
              : (() => {
            const rounds = previewSelection.form.roundsBySubmission ?? [];
            const latest = rounds.reduce(
              (max, r) => Math.max(max, r.submissionRound),
              Number.NEGATIVE_INFINITY
            );
            return previewSelection.round.submissionRound === latest;
          })()}
          onClose={() => {
            setPreviewSelection(null);
            setShowResubmitModal(false);
            setResubmitReason('');
          }}
          showResubmitModal={showResubmitModal}
          resubmitReason={resubmitReason}
          onResubmitReasonChange={setResubmitReason}
          onOpenResubmitModal={() => setShowResubmitModal(true)}
          onCloseResubmitModal={() => {
            setShowResubmitModal(false);
            setResubmitReason('');
          }}
          onApprove={async () => {
            const ok = await putFormReview(true, null);
            if (ok) {
              setPreviewSelection(null);
              loadForms();
            }
          }}
          onSaveResubmit={async () => {
            const ok = await putFormReview(false, resubmitReason.trim() || null);
            if (ok) {
              setShowResubmitModal(false);
              setResubmitReason('');
              setPreviewSelection(null);
              loadForms();
            }
          }}
        />
      )}

      {profilePreviewSection && submission?.email && (
        <ProfileSectionPreview
          section={profilePreviewSection}
          employeeEmail={submission.email}
          employeeName={submission.empName}
          invitationId={invitationId}
          readOnly={archiveMode}
          onClose={() => {
            setProfilePreviewSection(null);
            setShowResubmitModal(false);
            setResubmitReason('');
          }}
          showResubmitModal={showResubmitModal}
          resubmitReason={resubmitReason}
          onResubmitReasonChange={setResubmitReason}
          onOpenResubmitModal={() => setShowResubmitModal(true)}
          onCloseResubmitModal={() => {
            setShowResubmitModal(false);
            setResubmitReason('');
          }}
          onApprove={async () => {
            const ok = await putProfileSectionReview(profilePreviewSection, true, null);
            if (ok) {
              setProfilePreviewSection(null);
              loadForms();
            }
          }}
          onSaveResubmit={async () => {
            const ok = await putProfileSectionReview(
              profilePreviewSection,
              false,
              resubmitReason.trim() || null
            );
            if (ok) {
              setShowResubmitModal(false);
              setResubmitReason('');
              setProfilePreviewSection(null);
              loadForms();
            }
          }}
        />
      )}
    </>
  );
};

export default CheckSubmissionsFilledDocuments;
